import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useD324Query } from './hooks/useD324Query';
import type { ReportRow, CurrencySummary } from '../../services/d324Api';
import './D324Form.css';

const D324Form: React.FC = () => {
  const { loading, data, currencySummary, queried, noData, runQuery, reset } = useD324Query();

  const [reportType, setReportType] = useState<string>('bill');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [storeFrom, setStoreFrom] = useState('');
  const [storeTo, setStoreTo] = useState('');
  const [productFrom, setProductFrom] = useState('');
  const [productTo, setProductTo] = useState('');

  const handleQuery = () => {
    runQuery({
      report_type: reportType,
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      store_from: storeFrom.trim() || undefined,
      store_to: storeTo.trim() || undefined,
      product_from: productFrom.trim() || undefined,
      product_to: productTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setReportType('bill');
    setDateFrom(''); setDateTo('');
    setStoreFrom(''); setStoreTo('');
    setProductFrom(''); setProductTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const typeLabel = { bill: '單據別', date: '日期別', store: '庫位別', product: '產品別' }[reportType] || '';
    const title = `領用單明細表（${typeLabel}）`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>${title}</h2>
<table><thead><tr><th>單號</th><th>日期</th><th>庫位</th><th>庫位名稱</th><th>產品代號</th><th>品名</th><th>數量</th><th>幣別</th><th>單價</th><th>金額</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.FD150_01}</td><td>${r.FD150_12}</td><td>${r.FD150_13}</td><td>${r.FD150_14}</td><td>${r.product_code}</td><td>${r.FD151_12}</td><td class="right">${r.FD151_16}</td><td>${r.FD151_14}</td><td class="right">${r.FD151_15?.toFixed(2)}</td><td class="right">${r.FD151_17?.toFixed(2)}</td></tr>`).join('')}
</tbody></table><p>共 ${data.length} 筆</p></body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  const columns: ColumnsType<ReportRow> = [
    { title: '單號', dataIndex: 'FD150_01', key: 'FD150_01', width: 110 },
    { title: '領用日期', dataIndex: 'FD150_12', key: 'FD150_12', width: 100 },
    { title: '庫位', dataIndex: 'FD150_13', key: 'FD150_13', width: 80 },
    { title: '庫位名稱', dataIndex: 'FD150_14', key: 'FD150_14', ellipsis: true, width: 120 },
    { title: '產品代號', dataIndex: 'product_code', key: 'product_code', width: 130 },
    { title: '品名', dataIndex: 'FD151_12', key: 'FD151_12', ellipsis: true, width: 150 },
    { title: '數量', dataIndex: 'FD151_13', key: 'FD151_13', width: 80, align: 'right' as const },
    { title: '幣別', dataIndex: 'FD151_14', key: 'FD151_14', width: 60 },
    { title: '單價', dataIndex: 'FD151_15', key: 'FD151_15', width: 90, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '轉換數量', dataIndex: 'FD151_16', key: 'FD151_16', width: 90, align: 'right' as const },
    { title: '金額', dataIndex: 'FD151_17', key: 'FD151_17', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  const summaryColumns: ColumnsType<CurrencySummary> = [
    { title: '幣別', dataIndex: 'FD151_14', key: 'FD151_14', width: 80 },
    { title: '數量合計', dataIndex: 'total_qty', key: 'total_qty', align: 'right' as const },
    { title: '金額合計', dataIndex: 'total_amt', key: 'total_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.D324} action="query">
      <div className="d324-container">
        <div className="d324-header">
          <h2>領用單明細表</h2>
          {queried && !noData && <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('報表類型')}
            <Radio.Group value={reportType} onChange={e => setReportType(e.target.value)}>
              <Radio.Button value="bill">單據別</Radio.Button>
              <Radio.Button value="date">日期別</Radio.Button>
              <Radio.Button value="store">庫位別</Radio.Button>
              <Radio.Button value="product">產品別</Radio.Button>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>{filterLabel('領用日期起 (YYYY-MM-DD)')}<Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear /></div>
            <div>{filterLabel('領用日期迄 (YYYY-MM-DD)')}<Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear /></div>
            <div>{filterLabel('庫位代號起')}<Input value={storeFrom} onChange={e => setStoreFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear /></div>
            <div>{filterLabel('庫位代號迄')}<Input value={storeTo} onChange={e => setStoreTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear /></div>
            <div>{filterLabel('產品代號起')}<Input value={productFrom} onChange={e => setProductFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear /></div>
            <div>{filterLabel('產品代號迄')}<Input value={productTo} onChange={e => setProductTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear /></div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space><Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button><Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button></Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            <Table<ReportRow>
              columns={columns} dataSource={data}
              rowKey={(r, i) => `${r.FD150_01}-${r.product_code}-${i}`}
              loading={loading} size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 1100 }} style={{ marginBottom: 16 }}
            />
            {currencySummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>幣別合計</div>
                <Table<CurrencySummary>
                  columns={summaryColumns} dataSource={currencySummary}
                  rowKey="FD151_14" size="small" pagination={false} scroll={{ x: 400 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default D324Form;
